import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Plus, Pencil, Trash2, Truck, Mail, Phone, Users } from 'lucide-react';

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    company: '',
    name: '',
    email: '',
    anrede: 'Sehr geehrte Damen und Herren',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Lieferanten');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error('E-Mail ist ein Pflichtfeld');
      return;
    }
    // Mindestens Firma oder Name muss ausgefüllt sein
    if (!form.company.trim() && !form.name.trim()) {
      toast.error('Bitte Firma oder Ansprechpartner angeben');
      return;
    }

    try {
      if (editing) {
        await api.put(`/suppliers/${editing}`, form);
        toast.success('Lieferant aktualisiert');
      } else {
        await api.post('/suppliers', form);
        toast.success('Lieferant erstellt');
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (supplier) => {
    setForm({
      company: supplier.company || '',
      name: supplier.name || '',
      email: supplier.email || '',
      anrede: supplier.anrede || 'Sehr geehrte Damen und Herren',
      phone: supplier.phone || '',
      notes: supplier.notes || ''
    });
    setEditing(supplier.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Lieferant wirklich löschen?')) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success('Lieferant gelöscht');
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const resetForm = () => {
    setForm({
      company: '',
      name: '',
      email: '',
      anrede: 'Sehr geehrte Damen und Herren',
      phone: '',
      notes: ''
    });
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-suppliers">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Truck className="w-6 h-6 text-[#0E518D]" />
          <h1 className="text-2xl font-bold text-[#0E518D]">Lieferanten</h1>
        </div>
        <Button onClick={openCreate} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm" data-testid="add-supplier">
          <Plus className="w-4 h-4 mr-2" /> Lieferant hinzufügen
        </Button>
      </div>

      {suppliers.length === 0 ? (
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Keine Lieferanten vorhanden</p>
            <p className="text-sm text-slate-400 mt-1">Fügen Sie Ihren ersten Lieferanten hinzu</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {suppliers.map(supplier => (
            <Card key={supplier.id} className="bg-white rounded-sm shadow-sm border border-slate-100" data-testid={`supplier-${supplier.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1E293B]">{supplier.company || supplier.name}</h3>
                    {supplier.company && supplier.name && (
                      <p className="text-sm text-slate-600">Ansprechpartner: {supplier.name}</p>
                    )}
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {supplier.email}
                      </span>
                      {supplier.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {supplier.phone}
                        </span>
                      )}
                    </div>
                    {supplier.notes && (
                      <p className="text-sm text-slate-400 mt-2">{supplier.notes}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(supplier)} className="rounded-sm" data-testid={`edit-supplier-${supplier.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(supplier.id)} className="rounded-sm text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-supplier-${supplier.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Lieferant bearbeiten' : 'Neuer Lieferant'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Firma</Label>
              <Input
                value={form.company}
                onChange={e => setForm({ ...form, company: e.target.value })}
                placeholder="z.B. Sylter Landküche GmbH"
                className="mt-1 rounded-sm"
                data-testid="supplier-company"
              />
            </div>
            <div>
              <Label>Ansprechpartner (Name)</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Malte Müller"
                className="mt-1 rounded-sm"
                data-testid="supplier-name"
              />
            </div>
            <div>
              <Label>E-Mail *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="lieferant@example.de"
                className="mt-1 rounded-sm"
                data-testid="supplier-email"
              />
            </div>
            <div>
              <Label>Anrede für E-Mails</Label>
              <Input
                value={form.anrede}
                onChange={e => setForm({ ...form, anrede: e.target.value })}
                placeholder="z.B. Moin Malte oder Sehr geehrte Damen und Herren"
                className="mt-1 rounded-sm"
                data-testid="supplier-anrede"
              />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="+49 ..."
                className="mt-1 rounded-sm"
                data-testid="supplier-phone"
              />
            </div>
            <div>
              <Label>Notizen</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Interne Notizen..."
                className="mt-1 rounded-sm"
                rows={3}
                data-testid="supplier-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm" data-testid="supplier-save">
                {editing ? 'Speichern' : 'Erstellen'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
